import { useMemo, useState } from 'react';
import { CompositeIndexModel } from 'models';
import { MarketDateFilter } from 'models/types';
import {
    Box,
    Flex,
    Text,
    Card,
    CardHeader,
} from '@chakra-ui/react';
import RefreshBadge from 'components/RefreshBadge/RefreshBadge';
import IndexChartCore from './IndexChartCore';
import MetricCard from '../Index/MetricCard/MetricCard';
import CompositeIndexChartSkeleton from './Skeletons/CompositeIndexChartSkeleton';

interface CompositeIndexChartProps {
    compositeIndexData?: CompositeIndexModel;
    showLastCalculationDate?: boolean;
    useCard?: boolean;
    showTitle?: boolean;
    showFilters?: boolean;
    showMetricCard?: boolean;
    image?: string;
    loading?: boolean;
}

export default function CompositeIndexChart({ compositeIndexData, showLastCalculationDate, image, useCard = false, showTitle = true, showFilters = true, showMetricCard = true, loading = false }: CompositeIndexChartProps) {
    const [selectedRange, setSelectedRange] = useState<MarketDateFilter>(MarketDateFilter.MAX);

	const filteredBackfilled = useMemo(() => {
		return compositeIndexData?.filterDataByMarketDateFilter(
			selectedRange,
			true
		);
	}, [compositeIndexData, selectedRange]);

	const metric = filteredBackfilled?.length
		? filteredBackfilled[filteredBackfilled.length - 1].weightedIndex
		: 0;
	const priorMetric = filteredBackfilled?.length
		? filteredBackfilled[0].weightedIndex
		: undefined;


    const renderTitle = () => {
        return (
            <Flex direction={'row'} gap={4} alignItems={'center'}>
                <Text textAlign="left" fontSize='md' fontWeight='bold' mb='6px'>
                    {compositeIndexData?.name}
                </Text>
                {compositeIndexData?.recalculate && <RefreshBadge label={'Updating'} />}
            </Flex>
        );
    };

    function renderMetricCard() {
        if (showMetricCard && filteredBackfilled?.length) {
            return (
                <MetricCard name={compositeIndexData?.name || ''} image={image} metric={metric} priorMetricValue={priorMetric} showBorder={false} />
            )
        }
    }

    function renderLastCalculationDate() {
        return (
            <Text fontWeight='light' textAlign={'start'} fontSize={'xs'}>
                {compositeIndexData?.lastCalculationDate?.format('llll')}
            </Text>
        );
    }

    function renderChartContent() {
        return (
            <IndexChartCore indexData={compositeIndexData?.backfilledData || []} showFilters={showFilters} chartId={compositeIndexData?.id} onFilterChange={(filter) => setSelectedRange(filter)} />
        );
    }

    if (loading) {
        return (
            <CompositeIndexChartSkeleton useCard={useCard} showTitle={showTitle} showMetricCard={showMetricCard} showLastCalculationDate={showLastCalculationDate} />
        );
    } else if (useCard) {
        return (
            <Card paddingTop={7} paddingX={4} borderRadius={8} width={'100%'} display={'flex'} flexDirection={'column'}>
                <CardHeader paddingY={0}>
                    <Flex direction='column' alignSelf='flex-start' width={'100%'}>
                        {showTitle && renderTitle()}
                        {renderMetricCard()}
                        {showLastCalculationDate && renderLastCalculationDate()}
                    </Flex>
                </CardHeader>
                {renderChartContent()}
            </Card>
        );
    } else {
        return (
            <Box>
                <Flex direction='column' alignSelf='flex-start' width={'100%'}>
                    {showTitle && renderTitle()}
                    {renderMetricCard()}
                    {showLastCalculationDate && renderLastCalculationDate()}
                </Flex>
                {renderChartContent()}
            </Box>
        );
    }
}